import { apiFetch } from "./client";
import type {
  CaptureMode,
  Profile,
  Review,
  ReviewDocument,
  Run,
  Task,
  TaskDetail,
  Voice,
  BusinessRole,
  Fieldwork,
  CanonicalAnswer,
} from "./types";
const taskPath = (id: string) => `/studio/tasks/${encodeURIComponent(id)}`;
const json = (method: string, body: unknown) => ({
  method,
  body: JSON.stringify(body),
});
export const studio = {
  enableFieldwork: (id: string) =>
    apiFetch<Fieldwork>(taskPath(id) + "/fieldwork", json("POST", {})),
  fieldwork: (id: string) => apiFetch<Fieldwork>(taskPath(id) + "/fieldwork"),
  saveFieldwork: (
    id: string,
    phase: string,
    answers: CanonicalAnswer[],
    complete: boolean,
    expected_revision: number,
  ) =>
    apiFetch<Fieldwork>(
      taskPath(id) + "/fieldwork/" + phase,
      json("PUT", { answers, complete, expected_revision }),
    ),
  fieldworkSettings: (
    id: string,
    fw: Fieldwork,
    primary_mode: Fieldwork["primary_mode"],
    retest_plan: Fieldwork["retest_plan"],
  ) =>
    apiFetch<Fieldwork>(
      taskPath(id) + "/fieldwork",
      json("PATCH", {
        expected_revision: fw.revision,
        primary_mode,
        retest_plan,
      }),
    ),
  questionnaireLink: (id: string, rotate = false) =>
    apiFetch<{ url: string }>(
      taskPath(id) + "/fieldwork/link",
      json("POST", { rotate }),
    ),
  revokeQuestionnaireLink: (id: string) =>
    apiFetch<void>(taskPath(id) + "/fieldwork/link", { method: "DELETE" }),
  reminders: () =>
    apiFetch<{
      items: {
        task_id: string;
        title: string;
        retest_opens_at: string;
        retest_plan: string;
      }[];
    }>("/studio/fieldwork/reminders"),
  reminded: (id: string) =>
    apiFetch<Fieldwork>(taskPath(id) + "/fieldwork/reminded", json("POST", {})),
  tasks: (
    params: { archived?: boolean; limit?: number; offset?: number } = {},
  ) =>
    apiFetch<{ items: Task[]; total: number; limit: number; offset: number }>(
      "/studio/tasks?" +
        new URLSearchParams(
          Object.entries(params).map(([key, value]) => [key, String(value)]),
        ),
    ),
  createTask: (body: {
    client_task_id: string;
    title: string;
    capture_mode: CaptureMode;
  }) => apiFetch<Task>("/studio/tasks", json("POST", body)),
  task: (id: string) => apiFetch<TaskDetail>(taskPath(id)),
  patchTask: (
    task: Task,
    patch: {
      title?: string;
      status?: "in_progress" | "paused" | "stopped";
      capture_mode?: CaptureMode;
      archived?: boolean;
    },
  ) =>
    apiFetch<Task>(
      taskPath(task.id),
      json("PATCH", { expected_revision: task.revision, ...patch }),
    ),
  upload: (id: string, file: File) =>
    apiFetch<Task>(
      taskPath(id) + "/source/upload?filename=" + encodeURIComponent(file.name),
      {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
        timeoutMs: 180_000,
      },
    ),
  process: (id: string) =>
    apiFetch<Run>(taskPath(id) + "/process", json("POST", {})),
  preview: (id: string, generation: number, lease_token: string) =>
    apiFetch<Run>(
      taskPath(id) + "/preview",
      json("POST", { generation, lease_token }),
    ),
  saveReview: (
    id: string,
    expected_revision_id: string | null,
    expected_transcript_head_id: string | null,
    expected_transcript_result_sha256: string | null,
    document: ReviewDocument,
  ) =>
    apiFetch<Review>(
      taskPath(id) + "/review",
      json("PUT", {
        expected_revision_id,
        expected_transcript_head_id,
        expected_transcript_result_sha256,
        document,
      }),
    ),
  approve: (id: string, review: Review, idempotency_key: string) =>
    apiFetch<{
      approval_id: string;
      respondent_uid: string;
      review_revision_id: string;
      status: "completed";
    }>(
      taskPath(id) + "/approve",
      json("POST", {
        review_revision_id: review.id,
        source_sha256: review.source_sha256,
        roles_sha256: review.roles_sha256,
        idempotency_key,
      }),
    ),
  profile: () => apiFetch<Profile>("/studio/profile"),
  patchProfile: (body: {
    onboarding_completed?: boolean;
    preferences?: Profile["preferences"];
  }) => apiFetch<Profile>("/studio/profile", json("PATCH", body)),
  cities: (id: string, q: string, region_code: string) =>
    apiFetch<{
      items: { code: string; label: string; region_code: string }[];
      snapshot_sha256: string;
    }>(
      taskPath(id) +
        "/cities?" +
        new URLSearchParams({ q, region_code, limit: "20" }),
    ),
  addVoice: (
    file: File,
    name: string,
    speaker_role: BusinessRole,
    taskId?: string,
  ) =>
    apiFetch<Voice>(
      "/studio/profile/voices?" +
        new URLSearchParams({
          name,
          speaker_role,
          ...(taskId ? { task_id: taskId } : {}),
        }),
      {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
        timeoutMs: 180_000,
      },
    ),
  removeVoice: (id: string) =>
    apiFetch<void>("/studio/profile/voices/" + encodeURIComponent(id), {
      method: "DELETE",
    }),
};
